import '../recruit/common/Recruit.scss'
import './Apply.scss'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'
import MainURL from '../../MainURL'

interface BoardItem {
  id: number
  channel: 'FILE' | 'SITE'
  status: string
  statusLabel: string
  group: 'reviewing' | 'active' | 'closed'
  version: number
  appliedAt: string
  statusChangedAt: string
  applicant: {
    name: string
    age: number | null
    region: string
    careerYears: number
    education: string
    contact: { phone: string; email: string } | null
  }
  message: string
  files: { index: number; name: string; size: number }[]
  churchMemo: string | null
  interviewAt: string | null
  actions: string[]
}

interface Board {
  job: { title: string; church: string; region: string; path: string | null; deadlineAt: string | null; closed: boolean }
  viewer: string
  items: BoardItem[]
}

interface Snapshot {
  title?: string
  educationItems?: { schoolName: string; major: string; startDate: string; endDate: string; status: string }[]
  careerItems?: { companyName: string; position: string; startDate: string; endDate: string; description: string }[]
  hopeRoles?: string
  hopeDepartment?: string
  hopeLocation?: string
}

const ACTION_LABEL: Record<string, string> = {
  CONTACTED: '연락함',
  INTERVIEW: '면접 잡기',
  OFFERED: '청빙 제안',
  ON_HOLD: '보류',
  VIEWED: '검토 재개',
  REJECTED: '불합격',
}
const GROUPS: [string, string][] = [['all', '전체'], ['reviewing', '검토 중'], ['active', '진행 중'], ['closed', '종료']]
const formatSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`)

// 교회용 지원자 보드 (/apply/church/:token) — 사역자모아 계정 없이 지원 메일의 링크로 들어온다
// 이메일로는 첨부 없는 새 지원자 알림만 가고, 이력서는 이 화면에서 확인한다 (application-structure-final.md 4.4)
export default function ChurchApplyBoard() {
  const { token } = useParams()
  const base = `${MainURL}/applyrelay/church/${token}`
  const [board, setBoard] = useState<Board | null>(null)
  const [error, setError] = useState('')
  const [group, setGroup] = useState('all')
  const [openId, setOpenId] = useState<number | null>(null)
  const [snapshots, setSnapshots] = useState<Record<number, Snapshot>>({})
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = () => {
    axios.get(base)
      .then(res => setBoard(res.data))
      .catch(err => setError(err.response?.data?.error || '지원자 목록을 불러오지 못했습니다.'))
  }

  useEffect(load, [token])

  const replaceItem = (item: BoardItem) => {
    setBoard(prev => prev && { ...prev, items: prev.items.map(i => (i.id === item.id ? item : i)) })
  }

  const toggleResume = async (item: BoardItem) => {
    if (openId === item.id) {
      setOpenId(null)
      return
    }
    setOpenId(item.id)
    try {
      const res = await axios.get(`${base}/application/${item.id}`)
      replaceItem(res.data.item)
      setSnapshots(prev => ({ ...prev, [item.id]: res.data.resumeSnapshot }))
    } catch (err: any) {
      alert(err.response?.data?.error || '이력서를 불러오지 못했습니다.')
    }
  }

  const changeStatus = async (item: BoardItem, to: string) => {
    if (busyId) return
    let memo: string | null = null
    let interviewAt: string | null = null
    if (to === 'INTERVIEW') {
      interviewAt = window.prompt('면접 일시를 입력해 주세요. (예: 2026-10-20 14:00)', '')
      if (interviewAt === null) return
      interviewAt = interviewAt.trim().replace(' ', 'T') || null
    }
    if (['CONTACTED', 'INTERVIEW', 'OFFERED'].includes(to)) {
      memo = window.prompt('지원자에게 이메일로 함께 보낼 안내가 있으면 적어 주세요. (선택)', '')
      if (memo === null) return
    } else if (!window.confirm(`'${ACTION_LABEL[to]}'(으)로 바꿀까요?`)) {
      return
    }
    setBusyId(item.id)
    try {
      const res = await axios.patch(`${base}/application/${item.id}/status`, { to, version: item.version, memo, interviewAt })
      replaceItem(res.data.item)
    } catch (err: any) {
      alert(err.response?.data?.error || '상태를 바꾸지 못했습니다.')
      load()
    } finally {
      setBusyId(null)
    }
  }

  const items = board ? board.items.filter(i => group === 'all' || i.group === group) : []
  const count = (g: string) => (board ? board.items.filter(i => g === 'all' || i.group === g).length : 0)

  return (
    <div className="recruit">
      <div className="inner">
        <div className="subpage__main apply-page">
          {error && <p className="apply-page__empty">{error}</p>}
          {!error && !board && <p className="apply-page__empty">불러오는 중...</p>}
          {board && (
            <>
              <div className="apply-page__head">
                <span className="apply-page__eyebrow">지원자 관리 · {board.job.church}</span>
                <h3 className="main_title">{board.job.title}</h3>
                <p className="apply-page__lead">
                  {board.job.region}
                  {board.job.deadlineAt ? ` · 마감 ${board.job.deadlineAt}` : ''}
                  {board.job.closed ? ' · 마감됨' : ''}
                  {board.job.path && <> · <Link to={board.job.path}>공고 보기</Link></>}
                </p>
              </div>

              <div className="apply-page__note">
                사역자모아로 온 지원서는 이 화면에 모입니다. 이메일로는 새 지원자 알림만 가고, 이력서는 여기서 보고 내려받으실 수 있습니다.
                연락·면접 같은 진행 상태를 바꾸면 지원자에게 이메일로 안내됩니다. 이 링크는 {board.viewer}로 보낸 알림 메일에만 들어 있으니 외부에 공유하지 마세요.
              </div>

              <div className="apply-tabs">
                {GROUPS.map(([key, label]) => (
                  <button key={key} type="button" className={group === key ? 'active' : ''} onClick={() => setGroup(key)}>
                    {label} <span>{count(key)}</span>
                  </button>
                ))}
              </div>

              {items.length === 0 && <p className="apply-page__empty">아직 지원자가 없습니다.</p>}

              <ul className="apply-list">
                {items.map(item => {
                  const snap = snapshots[item.id]
                  return (
                    <li key={item.id} className="apply-card">
                      <div className="apply-card__top">
                        <div>
                          <b className="apply-card__name">{item.applicant.name}</b>
                          <span className="apply-card__badge">{item.channel === 'FILE' ? '이력서 파일' : '사이트 이력서'}</span>
                        </div>
                        <span className={`apply-status apply-status--${item.status}`}>{item.statusLabel}</span>
                      </div>
                      <div className="apply-card__meta">
                        지원 {item.appliedAt}
                        {item.applicant.age ? ` · ${item.applicant.age}세` : ''}
                        {item.applicant.region ? ` · ${item.applicant.region}` : ''}
                        {item.applicant.education ? ` · ${item.applicant.education}` : ''}
                        {item.applicant.careerYears ? ` · 경력 ${item.applicant.careerYears}년` : ''}
                      </div>
                      {item.applicant.contact ? (
                        <div className="apply-card__contact">
                          {item.applicant.contact.phone && <a href={`tel:${item.applicant.contact.phone}`}>{item.applicant.contact.phone}</a>}
                          {item.applicant.contact.email && <a href={`mailto:${item.applicant.contact.email}`}>{item.applicant.contact.email}</a>}
                        </div>
                      ) : (
                        <div className="apply-card__meta">실명·연락처는 '연락함'을 누르면 공개됩니다.</div>
                      )}
                      {item.message && <p className="apply-card__message">{item.message}</p>}
                      {item.files.length > 0 && (
                        <div className="apply-card__files">
                          {item.files.map(f => (
                            <a key={f.index} href={`${base}/file/${item.id}/${f.index}`} onClick={() => setTimeout(load, 1500)}>
                              {f.name} <span>{formatSize(f.size)}</span>
                            </a>
                          ))}
                        </div>
                      )}
                      {item.interviewAt && <div className="apply-card__meta">면접 {item.interviewAt}</div>}

                      {item.channel === 'SITE' && (
                        <button type="button" className="apply-link" onClick={() => toggleResume(item)}>
                          {openId === item.id ? '이력서 접기' : '이력서 보기'}
                        </button>
                      )}
                      {openId === item.id && snap && (
                        <div className="apply-resume">
                          {(snap.educationItems || []).filter(e => e.schoolName).length > 0 && (
                            <>
                              <h5>학력</h5>
                              <ul>
                                {(snap.educationItems || []).filter(e => e.schoolName).map((e, i) => (
                                  <li key={i}>{e.schoolName} {e.major} <span>{[e.startDate, e.endDate].filter(Boolean).join(' ~ ')} {e.status}</span></li>
                                ))}
                              </ul>
                            </>
                          )}
                          {(snap.careerItems || []).filter(c => c.companyName).length > 0 && (
                            <>
                              <h5>사역 이력</h5>
                              <ul>
                                {(snap.careerItems || []).filter(c => c.companyName).map((c, i) => (
                                  <li key={i}>{[c.companyName, c.employType && `[${c.employType}]`, c.position].filter(Boolean).join(' · ')} <span>{[c.startDate, c.endDate].filter(Boolean).join(' ~ ')}</span></li>
                                ))}
                              </ul>
                            </>
                          )}
                          {(snap.hopeRoles || snap.hopeDepartment || snap.hopeLocation) && (
                            <>
                              <h5>희망</h5>
                              <p>{[snap.hopeRoles, snap.hopeDepartment, snap.hopeLocation].filter(Boolean).join(' · ')}</p>
                            </>
                          )}
                        </div>
                      )}

                      {item.actions.length > 0 && (
                        <div className="apply-card__actions">
                          {item.actions.filter(a => ACTION_LABEL[a]).map(a => (
                            <button key={a} type="button" className={`apply-btn${a === 'CONTACTED' ? ' apply-btn--primary' : ''}`} disabled={busyId === item.id} onClick={() => changeStatus(item, a)}>
                              {ACTION_LABEL[a]}
                            </button>
                          ))}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
